<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { onOauthPopupResult, openOauthPopup, startOauthLogin } from '@drama/auth-client'
import { configuredOauthClients } from '../../lib/oauth-clients.js'
import { requestGoogleAccessToken } from '../../lib/google-identity.js'
import { OAUTH_PROVIDER_ICONS, OAUTH_PROVIDER_LABELS } from './oauth-icons.js'

// 默认只露前四家，其余折在「更多方式」后面，平台再多也不会堆成一面墙
const MAX_VISIBLE = 4

const props = defineProps({
  account: { type: Object, required: true },
  returnTo: { type: String, default: '' },
})
const emit = defineEmits(['error', 'signed-in'])

const { t } = useI18n()
const clients = configuredOauthClients()
const expanded = ref(false)
const visibleClients = computed(() => (expanded.value ? clients : clients.slice(0, MAX_VISIBLE)))
const hasMore = clients.length > MAX_VISIBLE

// 小窗结束后把结果通知回来：主页面负责关小窗（小窗自关在部分浏览器里会被忽略），成功算登录完成，失败在原页面提示
let activePopup = null
const stopListening = onOauthPopupResult(({ error }) => {
  activePopup?.close()
  activePopup = null
  if (error) emit('error', t('auth.social.failed'))
  else emit('signed-in')
})
onBeforeUnmount(stopListening)

async function handleClick(client) {
  const mobile = globalThis.matchMedia('(max-width: 680px)').matches
  // 桌面 Google 走 Google 自己的 SDK 弹窗（线上原有链路）：拿到 access_token 直接登录，页面不动
  if (client.provider === 'google' && !mobile) {
    try {
      const access_token = await requestGoogleAccessToken(client.client_id)
      await props.account.loginWithOauth({ provider: 'google', access_token })
      emit('signed-in')
    } catch {
      emit('error', t('auth.social.failed'))
    }
    return
  }
  // 其他平台桌面用我们自己的小窗完成授权；手机弹窗不可靠，整页跳转。小窗被拦截时也退回整页跳转
  const popup = mobile ? null : openOauthPopup()
  activePopup = popup
  try {
    await startOauthLogin({ provider: client.provider, client_id: client.client_id, return_to: props.returnTo, popup })
  } catch {
    popup?.close()
    activePopup = null
    emit('error', t('auth.social.failed'))
  }
}
</script>

<template>
  <div v-if="clients.length" class="start-auth-oauth">
    <button
      v-for="client in visibleClients"
      :key="client.provider"
      type="button"
      class="start-auth-oauth-button"
      :class="`is-${client.provider}`"
      @click="handleClick(client)"
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path v-for="path in OAUTH_PROVIDER_ICONS[client.provider]" :key="path.d" :d="path.d" :fill="path.fill" />
      </svg>
      <span>{{ t('auth.social.continueWith', { provider: OAUTH_PROVIDER_LABELS[client.provider] }) }}</span>
    </button>
    <button v-if="hasMore && !expanded" class="start-auth-oauth-more" type="button" @click="expanded = true">
      {{ t('auth.social.moreOptions') }}
    </button>
  </div>
</template>
